const STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "if",
  "then",
  "else",
  "when",
  "at",
  "by",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "to",
  "with",
  "as",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "it",
  "its",
  "that",
  "this",
  "those",
  "these",
  "we",
  "you",
  "they",
  "he",
  "she",
  "i",
  "me",
  "my",
  "your",
  "our",
  "their",
  "them",
  "his",
  "her",
  "ours",
  "yours",
]);

const SINGLE_WORD_THRESHOLD = 0.8;
const PHRASE_THRESHOLD = 0.85;

const normalize = (str: string) => {
  // normalize unicode, lowercase
  let s = str.normalize("NFKC").toLowerCase();
  // replace punctuation with spaces (keep letters/digits and spaces)
  s = s.replace(/[^\p{L}\p{N}\s]/gu, " ");
  // collapse whitespace
  s = s.replace(/\s+/g, " ").trim();
  return s;
};

const tokenizeNoStops = (str: string) => {
  const toks = normalize(str).split(" ").filter(Boolean);
  return toks.filter((t) => !STOP_WORDS.has(t));
};

const levenshtein = (a: string, b: string) => {
  const m = a.length,
    n = b.length;
  if (m === 0) {
    return n;
  }
  if (n === 0) {
    return m;
  }
  const dp = new Array(n + 1);
  for (let j = 0; j <= n; j++) {
    dp[j] = j;
  }
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[j] = Math.min(
        dp[j] + 1, // deletion
        dp[j - 1] + 1, // insertion
        prev + cost, // substitution
      );
      prev = tmp;
    }
  }
  return dp[n];
};

const similarityRatio = (a: string, b: string) => {
  if (!(a || b)) {
    return 1;
  }
  const dist = levenshtein(a, b);
  const denom = Math.max(a.length, b.length) || 1;
  return 1 - dist / denom; // 1 = identical, 0 = totally different
};

export const calculateMatchFuzzy = (text: string, keyword: string) => {
  // Prepare token stream of the text (stopwords removed)
  const textTokens = tokenizeNoStops(text);

  const qTokens = tokenizeNoStops(keyword);

  if (qTokens.length === 0) {
    return 0;
  }

  const isPhrase = qTokens.length > 1;
  const qStr = qTokens.join(" ");

  let best = { score: 0, matched: "", start: -1, end: -1 };

  // Sliding window over text tokens with window size ~= query tokens
  // We also allow +/- 1 token to be forgiving about stopword removal mismatch
  const sizes = new Set([qTokens.length, qTokens.length - 1, qTokens.length + 1]);
  for (const winSize of sizes) {
    if (winSize <= 0) {
      continue;
    }
    for (let i = 0; i + winSize <= textTokens.length; i++) {
      const windowStr = textTokens.slice(i, i + winSize).join(" ");
      const score = similarityRatio(qStr, windowStr);
      if (score > best.score) {
        best = { score, matched: windowStr, start: i, end: i + winSize - 1 };
      }
    }
  }

  const threshold = isPhrase ? PHRASE_THRESHOLD : SINGLE_WORD_THRESHOLD;

  return best.score > threshold;
};
